import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  DashboardService,
  GetAccountWidgetAbsencesResponse,
  UpdateWidgetAbsencesRequest,
} from '@app/gen/dashboard';
import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { Entity } from '@app/types';

import { SettingsWithRootState } from '../../../../slice';
import { SettingsState } from '../../../settings.state';

export const getAbsencesSettings = createAsyncThunk<
  GetAccountWidgetAbsencesResponse,
  { widgetId: string; accountId: string; companyId?: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getAbsencesSettings',
  async (
    { widgetId, accountId, companyId },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const result = await service.getAccountWidgetAbsences({
        params: {
          widget_id: widgetId,
          account_id: accountId,
        },
        ...(companyId && {
          query: {
            company_id: companyId,
          },
        }),
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateAbsencesSettings = createAsyncThunk<
  void,
  ThunkValuesAndActions<{
    widgetId: string;
    accountId: string;
    companyId?: string;
    body: UpdateWidgetAbsencesRequest;
  }>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/updateAbsencesSettings',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(DashboardService);

      const { widgetId, accountId, companyId, body } = values;

      await service.updateWidgetAbsences({
        params: {
          widget_id: widgetId,
          account_id: accountId,
        },
        ...(companyId && {
          query: {
            company_id: companyId,
          },
        }),
        body,
      });

      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Ближайшие отпуска',
            type: 'info',
          },
        ]),
      );
      actions.resolve(null);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsAbsencesReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getAbsencesSettings.pending, (state) => {
    state.absences.status = 'loading';
    state.absences.error = null;
  });
  builder.addCase(getAbsencesSettings.fulfilled, (state, { payload }) => {
    state.absences.status = 'complete';
    state.absences.isException = !!payload?.company_content_id;
    state.absences.data = payload;
  });
  builder.addCase(getAbsencesSettings.rejected, (state, { payload, error }) => {
    state.absences.status = 'failed';
    state.absences.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
